import usePlusMembershipStatus from 'components/Plus/hooks/usePlusMembershipStatus';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import getOpPlusV3Info from 'store/dashboard/selector/getOpPlusV3Info';

interface MembershipStatus {
  title?: string;
  description?: string;
  label?: string;
  className?: string;
  endDate?: string;
}

type MembershipStatusKeys =
  | 'ended'
  | 'withdrawn'
  | 'paymentFailed'
  | 'paymentFailedCancelled'
  | 'endingSoon'
  | 'legacyEndingSoon'
  | 'legacyEndingLongerThan30'
  | 'isFailedPaymentPausedFirst30Days';

interface MembershipStatuses extends Record<MembershipStatusKeys, MembershipStatus> {}

const usePlusMembershipStatusData = () => {
  const subscriptionData = useSelector(getOpPlusV3Info);
  const {
    isCancelled,
    isCancelledBeforeRenewal,
    isFailedPaymentCancelled,
    isMembershipWithdrawn,
    legacyExpirationDays,
    legacyExpiringSoon,
    isFailedPaymentPausedFirst30Days,
  } = usePlusMembershipStatus();
  const { t } = useTranslation();

  const membershipStatus: MembershipStatuses = {
    ended: {
      label: 'txt_plus_membership_ended',
      description: 'txt_plus_membership_ended_on',
      endDate: subscriptionData?.subscriptionInfo.endDateFormatted,
      className: 'error',
    },
    withdrawn: {
      label: 'txt_plus_membership_ended',
      description: 'txt_plus_membership_ended_on',
      endDate: subscriptionData?.subscriptionInfo.cancelDateFormatted,
      className: 'error',
    },
    paymentFailed: {
      label: 'txt_plus_membership_ended',
      description: 'txt_plus_payment_failed',
      className: 'error',
    },
    paymentFailedCancelled: {
      label: 'txt_plus_membership_ended',
      description: 'txt_plus_membership_ended_on',
      endDate: subscriptionData?.subscriptionInfo.cancelDateFormatted,
      className: 'info-outline',
    },
    endingSoon: {
      label: 'txt_plus_membership_going_to_end',
      description: 'txt_plus_membership_ending_soon',
      endDate: subscriptionData?.subscriptionInfo.endDateFormatted,
      className: 'warning',
    },
    legacyEndingSoon: {
      label: 'txt_plus_membership_going_to_end',
      description: 'txt_plus_membership_ending_in',
      endDate: legacyExpirationDays?.toString(),
      className: 'warning',
    },
    legacyEndingLongerThan30: {
      label: 'txt_plus_need_help',
    },
    isFailedPaymentPausedFirst30Days: {
      label: 'txt_plus_membership_ended',
      description: 'txt_plus_payment_failed',
      className: 'danger',
    },
  };

  const status = useMemo(() => {
    if (isCancelled) return membershipStatus.ended;
    if (isMembershipWithdrawn) return membershipStatus.withdrawn;
    if (isCancelledBeforeRenewal) return membershipStatus.endingSoon;
    if (legacyExpiringSoon) return membershipStatus.legacyEndingSoon;
    if (isFailedPaymentPausedFirst30Days) return membershipStatus.isFailedPaymentPausedFirst30Days;
    if (isFailedPaymentCancelled) return membershipStatus.paymentFailedCancelled;
    if (!legacyExpiringSoon) return membershipStatus.legacyEndingLongerThan30;

    return {
      label: t('txt_end_membership'),
      description: t('txt_end_membership_description'),
    };
  }, [isCancelled, isCancelledBeforeRenewal]);

  return status;
};

export default usePlusMembershipStatusData;
